/**
 * Building the document handed to the sandboxed mail iframe.
 *
 * The server has already sanitised `html`, so this is defence in depth — the iframe
 * itself (`sandbox=""`, no scripts, opaque origin) plus the CSP below are what make a
 * hostile message harmless even if the sanitiser were ever wrong.
 */

/** An <img ...> tag, tolerant of '>' inside quoted attribute values. */
const IMG_TAG = /<img\b(?:[^>"']|"[^"]*"|'[^']*')*>/gi;
const REMOTE_ATTR = /\sdata-remote-src\s*=\s*(?:"([^"]*)"|'([^']*)')/i;
const EMPTY_SRC = /\ssrc\s*=\s*(?:""|'')/i;

/** True when the message has at least one remote image the server withheld. */
export function hasRemoteImages(html: string | undefined): boolean {
  if (!html) return false;
  return (html.match(IMG_TAG) ?? []).some((tag) => REMOTE_ATTR.test(tag));
}

/**
 * Rewrites `data-remote-src="https://…"` to a real `src` on each image. Only https URLs are
 * promoted; anything else stays blocked. Runs on the string passed to srcdoc, never on a
 * live DOM, and only after the user asks for it.
 */
export function enableRemoteImages(html: string): string {
  return html.replace(IMG_TAG, (tag) => {
    const match = REMOTE_ATTR.exec(tag);
    if (!match) return tag;
    const url = match[1] ?? match[2] ?? '';
    if (!/^https:\/\//i.test(url)) return tag;
    const cleaned = tag.replace(REMOTE_ATTR, '').replace(EMPTY_SRC, '');
    return cleaned.replace(/^<img\b/i, `<img src="${url.replace(/"/g, '&quot;')}"`);
  });
}

export function buildContentSecurityPolicy(allowRemoteImages: boolean): string {
  return [
    "default-src 'none'",
    "script-src 'none'",
    `img-src data:${allowRemoteImages ? ' https:' : ''}`,
    "style-src 'unsafe-inline'",
    "form-action 'none'",
  ].join('; ');
}

/** Complete srcdoc document: CSP first, links open in a new context, images scaled to fit. */
export function buildSrcDoc(html: string, allowRemoteImages: boolean): string {
  const body = allowRemoteImages ? enableRemoteImages(html) : html;
  const csp = buildContentSecurityPolicy(allowRemoteImages);
  return (
    '<!doctype html><html><head><meta charset="utf-8">' +
    `<meta http-equiv="Content-Security-Policy" content="${csp}">` +
    '<meta name="referrer" content="no-referrer">' +
    '<base target="_blank">' +
    '<style>:root{color-scheme:light}body{margin:12px;font:14px/1.5 system-ui,sans-serif;overflow-wrap:anywhere}' +
    'img{max-width:100%;height:auto}table{max-width:100%}blockquote{margin-left:0;padding-left:12px;border-left:3px solid #ccc}</style>' +
    `</head><body>${body}</body></html>`
  );
}
